import React, {useState} from 'react'

import {Button, H4, P, Panel} from '../UI/index.js'
import {FormStack, Input, Select, Toggle} from '../UI/forms/index.js'
import OrderConditionGroupsEditor from '../conditions/OrderConditionGroupsEditor.js'
import {Customer, Reseller} from '../../../types/types.js'
import t from 'src/helpers/Languages.js'

type IdlSettings = Reseller['settings']['idl']

interface IdlSettingsPanelProps {
    reseller: Reseller
    customers: {[id: string]: Customer}
    onSave: (idlSettings: IdlSettings) => void
}

const defaultIdlSettings: IdlSettings = {
    active: false,
    apiKey: '',
    defaultContactPhone: '',
    packagingTypeKey: 'BIGBAG',
    forwardOrders: 'all',
    conditionGroups: [],
    matchAllConditions: true
}

export default function IdlSettingsPanel({reseller, customers, onSave}: IdlSettingsPanelProps) {
    const [idlSettings, setIdlSettings] = useState<IdlSettings>({...defaultIdlSettings, ...structuredClone(reseller.settings.idl)})
    const [settingsChanged, setSettingsChanged] = useState(false)

    const onChange = (changes: Partial<IdlSettings>) => {
        setIdlSettings({...idlSettings, ...changes})
        setSettingsChanged(true)
    }

    return (
        <Panel shadow style={{width: 800, padding: 24, height: 'fit-content'}}>
            <H4 style={{marginBottom: 36}}>IDL</H4>

            <FormStack>
                <Toggle
                    label={t('idlConnectionActive')}
                    checked={idlSettings.active}
                    onChange={(event) => onChange({active: !!event.target.checked})}
                />

                <Input
                    label={t('apiKey')}
                    value={idlSettings.apiKey}
                    onChange={(event) => onChange({apiKey: event.target.value})}
                />

                <Input
                    label={t('idlDefaultContactPhone')}
                    info={t('idlDefaultContactPhoneInfo')}
                    value={idlSettings.defaultContactPhone}
                    onChange={(event) => onChange({defaultContactPhone: event.target.value})}
                />

                <Input
                    label={t('idlPackagingTypeKey')}
                    info={t('idlPackagingTypeKeyInfo')}
                    value={idlSettings.packagingTypeKey}
                    onChange={(event) => onChange({packagingTypeKey: event.target.value})}
                />

                <Select
                    label={t('forwardOrders')}
                    value={idlSettings.forwardOrders}
                    options={[
                        {value: 'all', title: t('forwardAllOrders')},
                        {value: 'conditions', title: t('forwardOrdersMatchingConditions')}
                    ]}
                    onChange={(event) => onChange({forwardOrders: event.target.value as IdlSettings['forwardOrders']})}
                />

                {idlSettings.forwardOrders === 'conditions' &&
                    <>
                        <P>{t('forwardOrdersConditionsInfo')}</P>

                        <OrderConditionGroupsEditor
                            conditionGroups={idlSettings.conditionGroups}
                            matchAll={idlSettings.matchAllConditions}
                            reseller={reseller}
                            customers={customers || {}}
                            onChange={(conditionGroups, matchAllConditions) => onChange({conditionGroups, matchAllConditions})}
                        />
                    </>
                }
            </FormStack>

            <div style={{display: 'flex', justifyContent: 'flex-end', marginTop: 48}}>
                <Button
                    disabled={!settingsChanged}
                    onClick={() => {
                        onSave(idlSettings)
                        setSettingsChanged(false)
                    }}
                >
                    {t('save')}
                </Button>
            </div>
        </Panel>
    )
}
